import React from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import {
  Card,
  Title,
  Paragraph,
  Chip,
  Text,
  Avatar,
} from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { Cocktail } from '../services/ApiService';
import { useTheme } from '../contexts/ThemeContext';

interface EnhancedCocktailCardProps {
  cocktail: Cocktail;
  onPress: (cocktail: Cocktail) => void;
  showDetails?: boolean;
  onSearchTerm?: (term: string) => void;
}

const EnhancedCocktailCard: React.FC<EnhancedCocktailCardProps> = ({
  cocktail,
  onPress,
  showDetails = false,
  onSearchTerm,
}) => {
  const { themeData } = useTheme();
  
  console.log('🍹 Rendering cocktail card:', cocktail.name);

  const getStrengthColor = (strength?: string) => {
    switch (strength) {
      case 'low': return '#4CAF50';
      case 'standard': return '#FF9800';
      case 'strong': return '#F44336';
      default: return '#757575';
    }
  };



  const formatABV = (abv?: number | string) => {
    if (!abv) return '';
    const numAbv = typeof abv === 'string' ? parseFloat(abv) : abv;
    if (isNaN(numAbv)) return '';
    return `${numAbv.toFixed(1)}%`;
  };

  const getMethodIcon = (method?: string) => {
    switch (method?.toLowerCase()) {
      case 'shake': return 'wine';
      case 'stir': return 'refresh';
      case 'build': return 'construct';
      case 'blend': return 'cafe';
      case 'swizzle': return 'sync';
      default: return 'flask';
    }
  };

  const getGlassIcon = (glass?: string) => {
    switch (glass?.toLowerCase()) {
      case 'coupe': return 'wine';
      case 'rocks': return 'water';
      case 'highball': return 'beer';
      case 'collins': return 'water';
      case 'martini': return 'wine';
      case 'shot': return 'flask';
      default: return 'wine';
    }
  };

  return (
    <TouchableOpacity onPress={() => onPress(cocktail)}>
      <Card style={[styles.card, { backgroundColor: themeData.colors.cardBackground }]} elevation={3}>
        <Card.Content style={styles.content}>
          {/* Header with title and glassware icon */}
          <View style={styles.header}>
            <View style={styles.titleContainer}>
              <Title style={[styles.title, { color: themeData.colors.text }]} numberOfLines={2}>
                {cocktail.name}
              </Title>
                             {cocktail.family && (
                 <TouchableOpacity 
                   onPress={() => onSearchTerm?.(cocktail.family!)}
                   style={styles.chipTouchable}
                 >
                   <Chip 
                     mode="outlined" 
                     style={[styles.familyChip, { backgroundColor: themeData.colors.highlight1 + '20' }]}
                     textStyle={[styles.familyChipText, { color: themeData.colors.highlight1 }]}
                   >
                     {cocktail.family}
                   </Chip>
                 </TouchableOpacity>
               )}
            </View>
                         {/* Glassware and method icons in top right */}
             <View style={styles.iconsContainer}>
               <TouchableOpacity 
                 onPress={() => onSearchTerm?.(cocktail.method!)}
                 style={styles.iconTouchable}
               >
                 <Ionicons 
                   name={getMethodIcon(cocktail.method) as any} 
                   size={18} 
                   color={themeData.colors.highlight2} 
                   style={styles.methodIcon}
                 />
               </TouchableOpacity>
               <TouchableOpacity 
                 onPress={() => onSearchTerm?.(cocktail.glass!)}
                 style={styles.iconTouchable}
               >
                 <Ionicons 
                   name={getGlassIcon(cocktail.glass) as any} 
                   size={20} 
                   color={themeData.colors.highlight3} 
                 />
               </TouchableOpacity>
             </View>
          </View>

          {/* Description */}
          {cocktail.instructions && (
            <Paragraph style={[styles.description, { color: themeData.colors.textSecondary }]} numberOfLines={2}>
              {cocktail.instructions}
            </Paragraph>
          )}

          {/* IBA Category */}
          {cocktail.iba_status && cocktail.iba_status !== 'Unspecified' && (
            <View style={styles.ibaContainer}>
              <Chip 
                mode="outlined" 
                style={[styles.ibaChip, { backgroundColor: themeData.colors.highlight4 + '20' }]}
                textStyle={[styles.ibaChipText, { color: themeData.colors.highlight4 }]}
              >
                IBA {cocktail.iba_status}
              </Chip>
            </View>
          )}
        </Card.Content>
      </Card>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    marginBottom: 12,
    borderRadius: 12,
    backgroundColor: '#f8f8f8', // Changed from theme.colors.background
  },
  content: {
    padding: 12,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  titleContainer: {
    flex: 1,
    marginRight: 12,
  },
  title: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 6,
    lineHeight: 20,
    color: '#333', // Changed from theme.colors.text
  },
  familyChip: {
    alignSelf: 'flex-start',
    height: 24,
    backgroundColor: '#e0e0e0', // Changed from theme.colors.surface
  },
  familyChipText: {
    fontSize: 11,
    color: '#666', // Changed from theme.colors.textSecondary
  },
  iconsContainer: {
    marginLeft: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  methodIcon: {
    marginBottom: 2,
  },
  chipTouchable: {
    alignSelf: 'flex-start',
  },
  iconTouchable: {
    padding: 2,
  },

  description: {
    fontSize: 13,
    lineHeight: 16,
    marginBottom: 8,
    fontStyle: 'italic',
  },
  originContainer: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  originLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#666',
    marginRight: 4,
  },
  originText: {
    fontSize: 12,
    color: '#666',
    flex: 1,
  },
  ibaContainer: {
    marginTop: 4,
  },
  ibaChip: {
    alignSelf: 'flex-start',
    height: 20,
    backgroundColor: '#e0f7fa', // Changed from theme.colors.surface
  },
  ibaChipText: {
    fontSize: 11,
    color: '#00796b', // Changed from theme.colors.primary
  },

});

export default EnhancedCocktailCard;
